import { useEffect, useState } from 'react';
import { PackageCheck } from 'lucide-react';
import type { PickStopDto } from '@eombor/contracts';
import { Badge, Button, Dialog, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { Select } from '../../../shared/ui/Select.js';

/** Miqdorni ekranga chiqarish — `numeric(14,3)` bilan bir xil yumaloqlash. */
const round3 = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * Tayyor SABABLAR.
 *
 * Erkin matn ham qoladi, lekin ro'yxat birinchi turadi: skladchi
 * qo'lqopda va bir qo'lida skaner bilan ishlaydi — u yerda uzun jumla
 * yozilmaydi. Ro'yxatsiz maydon amalda «ok» va «yoq» bilan to'lardi.
 *
 * Kalitlar SAQLANMAYDI, matn saqlanadi: sabab hujjatga tushadi va uni
 * bir yildan keyin o'qiydigan odam kalitni ko'rmasligi kerak.
 */
const REASON_KEYS = ['shelfShort', 'damaged', 'notFound', 'takerRefused'] as const;

export interface PickTakeDialogProps {
  open: boolean;
  stop: PickStopDto | null;
  /**
   * Sabab MAJBURIY bo'lgan holat — skaner mos kelmagan bo'lsa.
   *
   * Miqdor to'liq bo'lsa ham sabab so'raladi: bu yerda chetlanish
   * miqdorda emas, TASDIQDA — skladchi skanerlash orqali isbotlamasdan
   * oldi.
   */
  forceReason?: boolean;
  onClose: () => void;
  onTake: (issuedQty: number, note: string | null) => void;
}

/**
 * «NECHTASINI OLDINGIZ?» — bitta o'rinni yopadigan oyna.
 *
 * Jimlikda REJADAGI son turadi va odatiy kunda skladchi unga umuman
 * tegmaydi: u shunchaki «Olindi» ni bosadi. Maydon esa javonda kam
 * chiqqan kun uchun ochiq turadi — ilgari bunday kunda berish butunlay
 * to'xtardi.
 *
 * Kam miqdor SABABSIZ o'tmaydi. Tekshiruv bu yerda ham, do'konda ham,
 * serverda ham bor va bu takror ataylab: e'tiroz kam berishga emas, uni
 * JIMGINA qilishga.
 */
export const PickTakeDialog = ({
  open,
  stop,
  forceReason = false,
  onClose,
  onTake,
}: PickTakeDialogProps) => {
  const [qty, setQty] = useState('');
  const [reasonKey, setReasonKey] = useState<string>('');
  const [freeText, setFreeText] = useState('');

  /* Oyna har ochilganda TOZA holatga qaytadi: oldingi o'rinning sababi
     keyingisiga yopishib qolsa, hujjatga yolg'on yozilardi. */
  useEffect(() => {
    if (!open || stop === null) return;
    setQty(String(round3(stop.qty)));
    setReasonKey('');
    setFreeText('');
  }, [open, stop]);

  if (stop === null) return null;

  const planned = round3(stop.qty);
  const value = Number(qty);
  const valid = Number.isFinite(value) && value > 0 && round3(value) <= planned;
  const short = valid && round3(value) < planned;
  const needsReason = short || forceReason;

  const note =
    reasonKey === 'other'
      ? freeText.trim()
      : reasonKey === ''
        ? ''
        : t(`pick.reason${reasonKey.charAt(0).toUpperCase()}${reasonKey.slice(1)}`);

  const blocked = !valid || (needsReason && note.length < 3);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={stop.nomenclatureName}
      description={t('pick.takeFrom', { cell: stop.cellCode })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('pick.cancel')}
          </Button>
          <Button
            disabled={blocked}
            onClick={() => onTake(round3(value), note === '' ? null : note)}
          >
            <PackageCheck size={15} aria-hidden />
            {t('pick.take')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <Badge tone="neutral" mono>
            {stop.batchNo}
          </Badge>
          <span className="text-sm text-text-muted">
            {t('pick.planned', { qty: String(planned), unit: unitText(stop.unit) })}
          </span>
        </div>

        <Input
          label={t('pick.takeQty')}
          type="number"
          inputMode="decimal"
          numeric
          min={0}
          max={planned}
          value={qty}
          suffix={unitText(stop.unit)}
          onChange={(event) => setQty(event.target.value)}
          {...(valid ? {} : { error: t('pick.takeQtyInvalid', { max: String(planned) }) })}
        />

        {/* Sabab maydoni FAQAT kerak bo'lganda chiziladi: odatiy berishda
            u ekranda turgan, lekin hech qachon to'ldirilmaydigan maydon
            bo'lardi va skladchi uni «ok» bilan to'ldirib o'rganib qolardi. */}
        {!needsReason ? null : (
          <div
            className={cn(
              'border-warn/30 bg-warn-soft flex flex-col gap-3',
              'rounded-[var(--radius-sm)] border p-3',
            )}
          >
            <p className="text-sm text-text">
              {forceReason && !short ? t('pick.reasonForced') : t('pick.reasonShort')}
            </p>

            <Select
              label={t('pick.reasonLabel')}
              value={reasonKey}
              options={[
                { value: '', label: t('pick.reasonPick') },
                ...REASON_KEYS.map((key) => ({
                  value: key,
                  label: t(`pick.reason${key.charAt(0).toUpperCase()}${key.slice(1)}`),
                })),
                { value: 'other', label: t('pick.reasonOther') },
              ]}
              onChange={(event) => setReasonKey(event.target.value)}
            />

            {reasonKey === 'other' ? (
              <Input
                label={t('pick.reasonText')}
                value={freeText}
                maxLength={200}
                onChange={(event) => setFreeText(event.target.value)}
              />
            ) : null}
          </div>
        )}
      </div>
    </Dialog>
  );
};
